/**
 * Mermaid `gantt` parser → a resolved task model (concrete start/end dates).
 *
 * Subset:
 *   title <t>, dateFormat <fmt> (assumed YYYY-MM-DD), section <name>
 *   task line:  `<name> : [done|active|crit|milestone,]* [id,] <start>, <dur|end>`
 *     start:    `YYYY-MM-DD` | `after <id> [<id>...]` | omitted (→ after previous task)
 *     dur|end:  `7d` | `2w` | `YYYY-MM-DD`
 * Unknown lines warn (never throw). Dependencies (`after`) become finish-to-start links.
 */

import { isISODate, isoToDayNum, addDaysISO, durationToDays } from './ganttDates'

export interface GanttTask {
  id: string
  name: string
  section: string
  startISO: string
  endISO: string // exclusive end (start + duration); === start for milestones
  isMilestone: boolean
  status: 'done' | 'active' | 'crit' | 'normal'
  deps: string[]
  progress?: number
}

export interface GanttModel {
  title?: string
  sections: string[]
  tasks: GanttTask[]
}

export interface GanttParseResult {
  model: GanttModel
  warnings: string[]
}

const TAGS = new Set(['done', 'active', 'crit', 'milestone'])
const DEFAULT_START = '2024-01-01'

export function parseMermaidGantt(text: string): GanttParseResult {
  const warnings: string[] = []
  const tasks: GanttTask[] = []
  const sections: string[] = []
  const byId = new Map<string, GanttTask>()
  let title: string | undefined
  let section = 'Tasks'
  let autoId = 0

  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim()
    if (!line || line.startsWith('%%')) continue
    if (/^gantt\b/.test(line)) continue
    if (/^(dateFormat|axisFormat|excludes|todayMarker|tickInterval|weekday)\b/i.test(line)) continue
    let m = line.match(/^title\s+(.+)$/i)
    if (m) { title = m[1].trim(); continue }
    m = line.match(/^section\s+(.+)$/i)
    if (m) { section = m[1].trim(); if (!sections.includes(section)) sections.push(section); continue }

    // task line
    const colon = line.indexOf(':')
    if (colon === -1) { warnings.push(`Unparsed line: ${line}`); continue }
    const name = line.slice(0, colon).trim()
    const fields = line.slice(colon + 1).split(',').map((s) => s.trim()).filter(Boolean)
    if (!name || !fields.length) { warnings.push(`Unparsed task: ${line}`); continue }

    // leading tags
    const tags: string[] = []
    while (fields.length && TAGS.has(fields[0].toLowerCase())) tags.push(fields.shift()!.toLowerCase())

    // optional leading id (not a date / after / duration)
    let id: string | undefined
    const looksTemporal = (f: string) => isISODate(f) || /^after\b/i.test(f) || /^\d+(?:\.\d+)?\s*[dwhm]?$/i.test(f)
    if (fields.length >= 2 && !looksTemporal(fields[0])) id = fields.shift()

    const endTok = fields.pop() ?? ''
    const startTok = fields.length ? fields[0] : null

    // resolve start
    const deps: string[] = []
    let startISO: string
    if (startTok && /^after\b/i.test(startTok)) {
      const refs = startTok.replace(/^after\s+/i, '').split(/\s+/).filter(Boolean)
      let maxEnd = DEFAULT_START
      for (const r of refs) {
        deps.push(r)
        const t = byId.get(r)
        if (t && isoToDayNum(t.endISO) > isoToDayNum(maxEnd)) maxEnd = t.endISO
      }
      startISO = maxEnd
    } else if (startTok && isISODate(startTok)) {
      startISO = startTok
    } else {
      // sequential: start where the previous task ended
      startISO = tasks.length ? tasks[tasks.length - 1].endISO : DEFAULT_START
    }

    // resolve end
    const isMilestone = tags.includes('milestone')
    let endISO: string
    if (isISODate(endTok)) endISO = endTok
    else endISO = addDaysISO(startISO, isMilestone ? 0 : durationToDays(endTok))

    const status: GanttTask['status'] = tags.includes('crit')
      ? 'crit'
      : tags.includes('active')
        ? 'active'
        : tags.includes('done')
          ? 'done'
          : 'normal'

    if (!sections.includes(section)) sections.push(section)
    const task: GanttTask = {
      id: id ?? `task-${autoId++}`,
      name,
      section,
      startISO,
      endISO,
      isMilestone,
      status,
      deps,
      progress: status === 'done' ? 100 : status === 'active' ? 50 : undefined,
    }
    tasks.push(task)
    byId.set(task.id, task)
  }

  return { model: { title, sections, tasks }, warnings }
}
